<div class="d-flex flex-wrap justify-content-between align-items-center mb-4">
  <div>
    <h5 class="mb-1 fw-bold">Companies</h5>
    <span class="sub-text">Manage billing companies for products</span>
  </div>
  <button class="btn btn-primary d-flex align-items-center gap-2" (click)="onAddCompany()">
    <i class="bi bi-plus-lg"></i>
    Add Company
  </button>
</div>

@if (isDataLoading) {
  <app-loading-spinner></app-loading-spinner>
} @else {
  @if (companies.length === 0) {
    <app-info-alert>No companies found. Add your first company to get started.</app-info-alert>
  } @else {
    <div class="table-responsive">
      <table class="table table-hover align-middle mb-0">
        <thead class="bg-light">
          <tr>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Name</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Code</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Email</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Contact</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">GST Number</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3">Website</th>
            <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (company of companies; track company.id) {
            <tr>
              <td class="py-3">
                <div class="fw-semibold">{{ company.name }}</div>
                @if (company.description) {
                  <div class="text-muted small mt-1">{{ company.description }}</div>
                }
                @if (company.address) {
                  <div class="text-muted small mt-1">
                    <i class="bi bi-geo-alt me-1"></i>
                    {{ company.address }}
                  </div>
                }
              </td>
              <td class="py-3">
                @if (company.code) {
                  <span class="badge bg-secondary fw-semibold">{{ company.code }}</span>
                } @else {
                  <span class="text-muted">—</span>
                }
              </td>
              <td class="py-3 small">
                @if (company.email) {
                  <div>
                    <i class="bi bi-envelope me-1 text-muted"></i>
                    {{ company.email }}
                  </div>
                } @else {
                  <span class="text-muted">—</span>
                }
              </td>
              <td class="py-3 small">
                @if (company.contact_number) {
                  <div class="mt-1">
                    <i class="bi bi-telephone me-1 text-muted"></i>
                    {{ company.contact_number }}
                  </div>
                } @else {
                  <span class="text-muted">—</span>
                }
              </td>
              <td class="py-3 small font-monospace">{{ company.gst_number || '—' }}</td>
              <td class="py-3 small">
                @if (company.website) {
                  <a [href]="company.website" target="_blank" rel="noopener" class="text-primary text-decoration-none">
                    <i class="bi bi-box-arrow-up-right me-1"></i>
                    Visit
                  </a>
                } @else {
                  <span class="text-muted">—</span>
                }
              </td>
              <td class="py-3 text-end">
                <div class="d-flex gap-2 justify-content-end">
                  <button class="btn btn-action-edit btn-sm" (click)="onEditCompany(company)" title="Edit company">
                    <i class="bi bi-pencil-square"></i>
                  </button>
                  <button
                    class="btn btn-action-delete btn-sm"
                    (click)="onDeleteCompany(company)"
                    title="Delete company">
                    <i class="bi bi-trash3"></i>
                  </button>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }
}

<ng-template #companyModal let-modal>
  <div class="modal-content border-0 shadow-lg">
    <form [formGroup]="companyForm" (ngSubmit)="onSubmit()">
      <div class="modal-header py-3">
        <h5 class="modal-title">{{ isEditMode ? 'Edit Company' : 'Add Company' }}</h5>
        <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
      </div>

      <div class="modal-body p-4">
        @if (formError) {
          <app-error-alert [error]="formError" />
        }

        <div class="mb-3">
          <label class="form-label small fw-bold text-muted">
            Company Name
            <span class="text-danger">*</span>
          </label>
          <input type="text" class="form-control" formControlName="name" placeholder="e.g. Agro Chemicals Pvt Ltd" />
          @if (isInvalid('name')) {
            <div class="text-danger small mt-1">Company name is required.</div>
          }
        </div>

        <div class="mb-3">
          <label class="form-label small fw-bold text-muted">Code</label>
          <input
            type="text"
            class="form-control text-uppercase"
            formControlName="code"
            placeholder="e.g. ECC"
            maxlength="20" />
          <div class="form-text text-muted">Short identifier used as prefix in order numbers (optional).</div>
          @if (isInvalid('code')) {
            <div class="text-danger small mt-1">Code must be 20 characters or fewer.</div>
          }
        </div>

        <div class="mb-3">
          <label class="form-label small fw-bold text-muted">Address</label>
          <textarea
            class="form-control"
            formControlName="address"
            rows="2"
            placeholder="Enter company address..."></textarea>
        </div>

        <div class="mb-3">
          <label class="form-label small fw-bold text-muted">Description</label>
          <textarea
            class="form-control"
            formControlName="description"
            rows="2"
            placeholder="Brief description of the company..."></textarea>
        </div>

        <div class="row g-3">
          <div class="col-md-6 mb-3 ms-0">
            <label class="form-label small fw-bold text-muted">Email</label>
            <input type="email" class="form-control" formControlName="email" placeholder="e.g. info@company.com" />
            @if (isInvalid('email')) {
              <div class="text-danger small mt-1">Enter a valid email address.</div>
            }
          </div>

          <div class="col-md-6 mb-3 me-0">
            <label class="form-label small fw-bold text-muted">Contact Number</label>
            <input
              type="text"
              class="form-control"
              formControlName="contact_number"
              placeholder="e.g. +91 9876543210" />
          </div>

          <div class="col-md-6 mb-3 ms-0">
            <label class="form-label small fw-bold text-muted">Website</label>
            <input type="text" class="form-control" formControlName="website" placeholder="e.g. https://company.com" />
          </div>

          <div class="col-md-6 mb-3 me-0">
            <label class="form-label small fw-bold text-muted">GST Number</label>
            <input
              type="text"
              class="form-control text-uppercase"
              formControlName="gst_number"
              placeholder="e.g. 27AAPFU0939F1ZV" />
          </div>
        </div>
      </div>

      <div class="modal-footer border-0">
        <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
        <button type="submit" class="btn btn-primary" [disabled]="companyForm.invalid || isSaving">
          @if (isSaving) {
            <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Saving...
          } @else {
            {{ isEditMode ? 'Update' : 'Create' }}
          }
        </button>
      </div>
    </form>
  </div>
</ng-template>
